import { Css } from "@homebound/beam";
import { FormattedDate } from "src/components/FormattedDate";
import { ImpactCard } from "src/routes/projects/change-events/guided/components/ImpactCard";
import { pluralize } from "src/utils";
import { ReviewImpactedPrice } from "../../components/ReviewImpactedPrice";
import { mockScheduleImpactMilestones } from "./ScheduleImpactTable";

type ProjectImpactCardsProps = {
  totals: { currentBudgetInCents: number; newBudgetInCents: number };
  /** How many project items the project has today, i.e. before the configuration is applied. */
  currentItemCount: number;
  /** How many project items the project would have after it. */
  newItemCount: number;
};

/** The "Budget / Scope / Schedule Impact" cards at the top of the Review Impact step. */
export function ProjectImpactCards(props: ProjectImpactCardsProps) {
  const { totals, currentItemCount, newItemCount } = props;
  const { currentBudgetInCents, newBudgetInCents } = totals;
  const budgetDifferenceInCents = newBudgetInCents - currentBudgetInCents;
  const itemDifference = newItemCount - currentItemCount;

  return (
    <div css={Css.dg.gtc("repeat(3, 1fr)").gap3.$}>
      <ImpactCard
        icon="invoiceDollar"
        title="Budget Impact"
        headline={<ReviewImpactedPrice valueInCents={budgetDifferenceInCents} />}
        rows={[
          {
            label: "Current Budget",
            value: <ReviewImpactedPrice valueInCents={currentBudgetInCents} showZeroAsNone hideDisplayDirection />,
          },
          {
            label: "New Budget",
            value: <ReviewImpactedPrice valueInCents={newBudgetInCents} showZeroAsNone hideDisplayDirection />,
          },
          {
            label: "Difference",
            value: (
              <ReviewImpactedPrice
                valueInCents={budgetDifferenceInCents}
                comparedValueInCents={currentBudgetInCents}
                showZeroAsNone
              />
            ),
          },
        ]}
      />
      <ImpactCard
        icon="list"
        title="Scope Impact"
        headline={`${itemDifference > 0 ? "+" : ""}${itemDifference} ${pluralize(itemDifference, "item")}`}
        rows={[
          {
            label: "Current Scope",
            value: currentItemCount === 0 ? "None" : `${currentItemCount} ${pluralize(currentItemCount, "item")}`,
          },
          {
            label: "New Scope",
            value: newItemCount === 0 ? "None" : `${newItemCount} ${pluralize(newItemCount, "item")}`,
          },
          {
            label: "Difference",
            value: currentItemCount === newItemCount ? "None" : `${itemDifference > 0 ? "+" : ""}${itemDifference}`,
          },
        ]}
      />
      <ScheduleImpactCard />
    </div>
  );
}

/** Mocked alongside `ScheduleImpactTable` — see the TODO there. */
function ScheduleImpactCard() {
  const milestones = mockScheduleImpactMilestones();
  const completion = milestones.last!;
  const changeInDays = completion.changeInDays;

  return (
    <ImpactCard
      icon="calendar"
      title="Schedule Impact"
      headline={`+${changeInDays} ${pluralize(changeInDays, "day")}`}
      rows={[
        { label: "Current Completion", value: <FormattedDate date={completion.currentFinish} /> },
        {
          label: "New Completion",
          value: <FormattedDate date={completion.currentFinish.add({ days: changeInDays })} />,
        },
        { label: "Difference", value: `+${changeInDays} ${pluralize(changeInDays, "day")}` },
      ]}
    />
  );
}
